import { APIProvider as VisGlApiProvider } from "@vis.gl/react-google-maps";
import { lazy, Suspense } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import { useIntl } from "react-intl";
import type { SearchResponse } from "./api/SearchResponse";
import {
  MapErrorFallback,
  MapLoadError,
} from "./components/DefaultErrorBoundary/DefaultErrorBoundary";
import { GOOGLE_MAPS_KEY } from "./components/Map/constants";
import { MapLoadingPlaceholder } from "./components/Map/MapLoadingPlaceholder";
import type { ReturnStage } from "./utils/hooks/useTypedLocation";
import logBreadcrumbs from "./utils/logBreadcrumbs";
import logError from "./utils/logError";
import { isHandledImportError } from "./utils/vitePreloadErrorHandler";
import { useLanguageCode } from "./utils/hooks/useLanguageCode";
import { useFeature } from "./feature/useFeature";

type Props = {
  shouldRenderMap: boolean;
  searchResponse?: SearchResponse;
  returnsFlowLocation?: ReturnStage;
};

export default function MapPane({
  shouldRenderMap,
  searchResponse,
  returnsFlowLocation,
}: Props) {
  return (
    <ErrorBoundary
      FallbackComponent={MapErrorFallback}
      onError={(error) => {
        if (isHandledImportError(error)) {
          return;
        }
        // Map load errors are often cause by user network problems, so
        // are not critical
        if (error instanceof MapLoadError) {
          console.warn(error);
          return;
        }
        logError(error);
      }}
    >
      <MapsApiProvider>
        <MapLoadingPlaceholder />
        <Suspense fallback={null}>
          {shouldRenderMap && searchResponse && (
            <LazyMap
              searchResponse={searchResponse}
              returnsFlowLocation={returnsFlowLocation}
            />
          )}
        </Suspense>
      </MapsApiProvider>
    </ErrorBoundary>
  );
}

let mapLanguage: string | undefined;

export function MapsApiProvider({ children }: { children: React.ReactNode }) {
  const languageCode = useLanguageCode();
  const { showBoundary } = useErrorBoundary();
  const intl = useIntl();

  // Choose the version. `undefined` will result in the default for our map id.
  // https://developers.google.com/maps/documentation/javascript/versions
  const mapsApiVersion = useFeature("GoogleMapsChannel");

  // Use Intl to get the relevant region to pass in
  const region = new Intl.Locale(intl.locale)?.region;

  // VisGl complains massively if we ever change the language after the map api has first loaded.
  // Using a global variable means even with multiple MapsApiProvider components the same language is used.
  // See - https://visgl.github.io/react-google-maps/docs/api-reference/components/api-provider
  mapLanguage ??= languageCode;

  return (
    <VisGlApiProvider
      apiKey={GOOGLE_MAPS_KEY}
      version={mapsApiVersion}
      language={mapLanguage}
      region={region}
      libraries={["geometry"]}
      onError={(error) =>
        showBoundary(new MapLoadError("Failed to load map api", error))
      }
    >
      {children}
    </VisGlApiProvider>
  );
}

export const LazyMap = lazy(async () => {
  logBreadcrumbs("Map", "Requesting Map chunk");
  return await import(
    /* webpackChunkName: "Map" */
    "./components/Map/Map"
  );
});
